import { Plus, X, DollarSign } from 'lucide-react';
import { TextInput, NumericInput, SelectInput } from '../inputs';
import type { InvoiceLineItem, ItemIdentification, TaxDetail, AllowanceCharge } from '../../types/invoice';

const UOM_OPTIONS = [
  { value: 'EA', label: 'Each' },
  { value: 'PC', label: 'Piece' },
  { value: 'BX', label: 'Box' },
  { value: 'CA', label: 'Case' },
  { value: 'LB', label: 'Pound' },
  { value: 'KG', label: 'Kilogram' },
];

const ITEM_IDENTIFICATION_OPTIONS = [
  { value: 'UP', label: 'UPC' },
  { value: 'UK', label: 'GTIN' },
  { value: 'BP', label: 'Buyer Part Number' },
  { value: 'VP', label: 'Vendor Part Number' },
  { value: 'SK', label: 'SKU' },
  // WDEV-142 (Natalie I6): let operators tag a line with the Engineering
  // Change Level. ASN's LineItemSection already offers this qualifier.
  { value: 'EC', label: 'Engineering Change Level' },
];

const TAX_OPTIONS = [
  { value: 'ST', label: 'State Tax' },
  { value: 'LT', label: 'Local Tax' },
  { value: 'GST', label: 'Goods and Services Tax' },
  { value: 'VAT', label: 'Value Added Tax' },
];

const ALLOWANCE_CHARGE_INDICATOR_OPTIONS = [
  { value: 'A', label: 'Allowance' },
  { value: 'C', label: 'Charge' },
];

const ALLOWANCE_CHARGE_CODE_OPTIONS = [
  { value: 'G830', label: 'Shipping and Handling' },
  { value: 'A470', label: 'Art Work' },
  { value: 'H170', label: 'Special Tooling Charge' },
  { value: 'F550', label: 'Premium Charge' },
];

interface InvoiceLineItemSectionProps {
  lineItem: InvoiceLineItem;
  index: number;
  errors: Record<string, string>;
  onChange: (field: keyof InvoiceLineItem, value: any) => void;
  onRemove: () => void;
  onCalculate: () => void;
}

export function InvoiceLineItemSection({
  lineItem,
  index,
  errors,
  onChange,
  onRemove,
  onCalculate,
}: InvoiceLineItemSectionProps) {
  const handleItemIdentificationChange = (idx: number, field: keyof ItemIdentification, value: string) => {
    const updated = [...lineItem.parts];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange('parts', updated);
  };

  const handleAddItemIdentification = () => {
    onChange('parts', [...lineItem.parts, { qualifier: 'UP', id: '' }]);
  };

  const handleRemoveItemIdentification = (idx: number) => {
    onChange('parts', lineItem.parts.filter((_, i) => i !== idx));
  };

  const handleTaxChange = (idx: number, field: keyof TaxDetail, value: string) => {
    const updated = [...lineItem.taxes];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange('taxes', updated);
  };

  const handleAddTax = () => {
    onChange('taxes', [...lineItem.taxes, { type: 'ST', amount: '', percent: '' }]);
  };

  const handleRemoveTax = (idx: number) => {
    onChange('taxes', lineItem.taxes.filter((_, i) => i !== idx));
  };

  const handleAllowanceChargeChange = (idx: number, field: keyof AllowanceCharge, value: string) => {
    const updated = [...lineItem.allowancesCharges];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange('allowancesCharges', updated);
  };

  const handleAddAllowanceCharge = () => {
    onChange('allowancesCharges', [...lineItem.allowancesCharges, { indicator: 'C', code: 'G830', amount: '' }]);
  };

  const handleRemoveAllowanceCharge = (idx: number) => {
    onChange('allowancesCharges', lineItem.allowancesCharges.filter((_, i) => i !== idx));
  };

  return (
    <div className="border border-gray-300 rounded-lg p-4 space-y-4 bg-gray-50">
      <div className="flex items-center justify-between gap-3">
        {/* WDEV-51: line number is now editable — matches the ASN pattern. */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Line #</label>
          <input
            type="number"
            min={1}
            value={lineItem.lineNumber}
            onChange={(e) => onChange('lineNumber', parseInt(e.target.value, 10) || 1)}
            className="w-20 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            aria-label={`Line number for line ${index + 1}`}
          />
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-600 hover:text-red-700"
          aria-label="Remove line"
        >
          <X size={20} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <NumericInput
          label="Quantity"
          value={lineItem.quantity}
          onChange={(value) => {
            onChange('quantity', value);
            setTimeout(onCalculate, 0);
          }}
          error={errors[`lineItem_${index}_quantity`]}
          required
        />

        <SelectInput
          label="UOM"
          value={lineItem.uom}
          onChange={(value) => onChange('uom', value)}
          options={UOM_OPTIONS}
          error={errors[`lineItem_${index}_uom`]}
        />

        <NumericInput
          label="Unit Price"
          value={lineItem.unitPrice}
          onChange={(value) => {
            onChange('unitPrice', value);
            setTimeout(onCalculate, 0);
          }}
          error={errors[`lineItem_${index}_unitPrice`]}
          required
        />

        <div className="relative">
          <NumericInput
            label="Extended Amount"
            value={lineItem.extendedAmount}
            onChange={(value) => onChange('extendedAmount', value)}
            error={errors[`lineItem_${index}_extendedAmount`]}
            required
          />
          <DollarSign className="absolute right-3 top-9 w-4 h-4 text-gray-400" />
        </div>
      </div>

      <TextInput
        label="Description"
        value={lineItem.description || ''}
        onChange={(value) => onChange('description', value)}
        error={errors[`lineItem_${index}_description`]}
      />

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">Item Identifications</label>
          <button
            type="button"
            onClick={handleAddItemIdentification}
            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <Plus size={12} />
            Add ID
          </button>
        </div>
        <div className="space-y-2">
          {lineItem.parts.map((item, idx) => (
            <div key={idx} className="flex gap-2">
              <div className="flex-1">
                <SelectInput
                  label=""
                  value={item.qualifier}
                  onChange={(value) => handleItemIdentificationChange(idx, 'qualifier', value)}
                  options={ITEM_IDENTIFICATION_OPTIONS}
                />
              </div>
              <div className="flex-[2]">
                <TextInput
                  label=""
                  value={item.id}
                  onChange={(value) => handleItemIdentificationChange(idx, 'id', value)}
                  placeholder="Item ID"
                />
              </div>
              <button
                type="button"
                onClick={() => handleRemoveItemIdentification(idx)}
                className="text-red-600 hover:text-red-700 pt-2"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">Taxes</label>
          <button
            type="button"
            onClick={handleAddTax}
            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <Plus size={12} />
            Add Tax
          </button>
        </div>
        <div className="space-y-2">
          {lineItem.taxes.map((tax, idx) => (
            <div key={idx} className="flex gap-2">
              <div className="flex-1">
                <SelectInput
                  label=""
                  value={tax.type}
                  onChange={(value) => handleTaxChange(idx, 'type', value)}
                  options={TAX_OPTIONS}
                />
              </div>
              <div className="flex-1">
                <NumericInput
                  label=""
                  value={tax.percent || ''}
                  onChange={(value) => handleTaxChange(idx, 'percent', value)}
                  placeholder="Percent"
                />
              </div>
              <div className="flex-1">
                <NumericInput
                  label=""
                  value={tax.amount}
                  onChange={(value) => handleTaxChange(idx, 'amount', value)}
                  placeholder="Amount"
                />
              </div>
              <button
                type="button"
                onClick={() => handleRemoveTax(idx)}
                className="text-red-600 hover:text-red-700 pt-2"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">Allowances & Charges</label>
          <button
            type="button"
            onClick={handleAddAllowanceCharge}
            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <Plus size={12} />
            Add A/C
          </button>
        </div>
        <div className="space-y-2">
          {lineItem.allowancesCharges.map((ac, idx) => (
            <div key={idx} className="flex gap-2">
              <div className="flex-1">
                <SelectInput
                  label=""
                  value={ac.indicator}
                  onChange={(value) => handleAllowanceChargeChange(idx, 'indicator', value as 'A' | 'C')}
                  options={ALLOWANCE_CHARGE_INDICATOR_OPTIONS}
                />
              </div>
              <div className="flex-1">
                <SelectInput
                  label=""
                  value={ac.code}
                  onChange={(value) => handleAllowanceChargeChange(idx, 'code', value)}
                  options={ALLOWANCE_CHARGE_CODE_OPTIONS}
                />
              </div>
              <div className="flex-1">
                <NumericInput
                  label=""
                  value={ac.amount}
                  onChange={(value) => handleAllowanceChargeChange(idx, 'amount', value)}
                  placeholder="Amount"
                />
              </div>
              <button
                type="button"
                onClick={() => handleRemoveAllowanceCharge(idx)}
                className="text-red-600 hover:text-red-700 pt-2"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
